import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useState } from "react";
import { userNavItems, adminNavItems, getUserProfileUrl } from "@/config/navigation";

interface UserMenuProps {
  isMobile?: boolean;
}

const UserMenu = ({
  isMobile = false
}: UserMenuProps) => {
  const {
    user,
    signOut,
    isAdmin
  } = useAuth();
  const navigate = useNavigate();
  const [isSigningOut, setIsSigningOut] = useState(false);

  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);
      await signOut();
    } catch (error) {
      console.error("Error signing out:", error);
    } finally {
      setIsSigningOut(false);
    }
  };

  // Find item by href helper
  const findItemByHref = (items: typeof userNavItems, href: string) => {
    return items.find(item => item.href === href);
  };

  // Non-authenticated state
  if (!user) {
    if (isMobile) {
      return <div className="space-y-3">
          <Button asChild variant="outline" className="w-full justify-start">
            <Link to="/sign-in" className="flex items-center gap-2">
              <LogIn className="h-4 w-4" />
              Sign In
            </Link>
          </Button>
          <Button asChild className="w-full justify-start">
            <Link to="/sign-up">Sign Up</Link>
          </Button>
        </div>;
    }
    return <div className="flex items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/sign-in" className="flex items-center gap-2">
            <LogIn className="h-4 w-4" />
            Sign In
          </Link>
        </Button>
        <Button asChild size="sm">
          <Link to="/sign-up">Sign Up</Link>
        </Button>
      </div>;
  }

  // Authenticated state - mobile view
  if (isMobile) {
    return <div className="space-y-3">
        {userNavItems.map(item => <Button key={item.href} asChild variant="outline" className="w-full justify-start">
            <Link to={item.href === "/profile" ? getUserProfileUrl(user.id) : item.href} className="flex items-center gap-2">
              {item.icon && <item.icon className="h-4 w-4" />}
              {item.title}
            </Link>
          </Button>)}

        {isAdmin && adminNavItems.map(item => <Button key={item.href} asChild variant="outline" className="w-full justify-start">
            <Link to={item.href} className="flex items-center gap-2">
              {item.icon && <item.icon className="h-4 w-4" />}
              {item.title}
            </Link>
          </Button>)}

        <Button variant="destructive" className="w-full justify-start" onClick={handleSignOut} disabled={isSigningOut}>
          {isSigningOut ? 'Signing Out...' : 'Sign Out'}
        </Button>
      </div>;
  }

  // Authenticated state - desktop view
  const profileItem = findItemByHref(userNavItems, "/profile");
  return <div className="flex items-center gap-3">
      <Button asChild variant="ghost" size="sm">
        <Link to="/dashboard">Dashboard</Link>
      </Button>
      
      <Button asChild variant="ghost" size="sm">
        <Link to={getUserProfileUrl(user.id)} className="flex items-center gap-1">
          {profileItem?.icon && <profileItem.icon className="h-4 w-4 mr-1" />}
          Profile
        </Link>
      </Button>
      <Button variant="ghost" size="sm" onClick={handleSignOut} disabled={isSigningOut}>
        {isSigningOut ? 'Signing Out...' : 'Sign Out'}
      </Button>
    </div>;
};

export default UserMenu;
